import { useEffect, useState } from 'react'
import { Button, Form, Input, Typography } from 'antd'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faBank, faLock } from '@fortawesome/pro-solid-svg-icons'
import { useMerchantApplication } from '../MerchantApplicationContext'
import EducationBlock from '../components/EducationBlock'
import StepFooter from '../components/StepFooter'

const { Title, Text } = Typography

type Props = {
  onNext: () => void
  onBack: () => void
}

type EntryMethod = 'plaid' | 'manual' | null

const BankAccountStep = ({ onNext, onBack }: Props) => {
  const { state, dispatch } = useMerchantApplication()
  const { bankAccount } = state
  const [form] = Form.useForm()
  const [entryMethod, setEntryMethod] = useState<EntryMethod>(
    bankAccount.routingNumber ? 'manual' : null
  )

  const updateField = (field: keyof typeof bankAccount, value: string) => {
    dispatch({ type: 'UPDATE_BANK_ACCOUNT', field, value })
  }

  useEffect(() => {
    form.setFieldsValue(bankAccount)
  }, [form, bankAccount.bankName, bankAccount.routingNumber, bankAccount.accountNumber, bankAccount.confirmAccountNumber])

  const handleNext = () => {
    if (entryMethod === 'manual') {
      form.validateFields().then(() => {
        onNext()
      }).catch(() => {
        // validation errors shown inline
      })
    } else {
      onNext()
    }
  }

  return (
    <>
      <div className="merchant-app-step-header">
        <Title level={3}>Bank Account</Title>
        <Text type="secondary">
          Connect the business bank account where your payment deposits will be sent.
        </Text>
      </div>
      <div className="merchant-app-step-body">
        {/* Plaid option */}
        <div
          className={`merchant-app-bank-option ${entryMethod === 'plaid' ? 'selected' : ''}`}
          onClick={() => setEntryMethod('plaid')}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <div style={{
              width: 44, height: 44, borderRadius: 8,
              background: entryMethod === 'plaid' ? '#fff7f5' : '#f5f5f5',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: entryMethod === 'plaid' ? '#FF5C35' : '#8c8c8c',
              fontSize: 18,
            }}>
              <FontAwesomeIcon icon={faBank} />
            </div>
            <div>
              <strong>Connect your bank</strong><br />
              <Text type="secondary" style={{ fontSize: 13 }}>
                Securely sign in to your bank to connect your account. This is the fastest way to get set up.
              </Text>
            </div>
          </div>
          {entryMethod === 'plaid' && (
            <div style={{ marginTop: 16, padding: 16, background: '#f5f5f5', borderRadius: 8, textAlign: 'center' }}>
              <FontAwesomeIcon icon={faLock} style={{ color: '#8c8c8c', marginRight: 8 }} />
              <Text type="secondary">
                Bank connection integration will be available when the backend is ready.
              </Text>
              <br />
              <Button
                type="primary"
                style={{ marginTop: 12 }}
                disabled
              >
                Connect Bank Account (Coming Soon)
              </Button>
            </div>
          )}
        </div>

        {/* Divider */}
        <div style={{ textAlign: 'center', margin: '8px 0', color: '#8c8c8c', fontSize: 13 }}>
          — or —
        </div>

        {/* Manual entry option */}
        <div
          className={`merchant-app-bank-option ${entryMethod === 'manual' ? 'selected' : ''}`}
          onClick={() => {
            if (entryMethod !== 'manual') {
              setEntryMethod('manual')
            }
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: entryMethod === 'manual' ? 16 : 0 }}>
            <div style={{
              width: 44, height: 44, borderRadius: 8,
              background: entryMethod === 'manual' ? '#fff7f5' : '#f5f5f5',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: entryMethod === 'manual' ? '#FF5C35' : '#8c8c8c',
              fontSize: 18,
            }}>
              <FontAwesomeIcon icon={faLock} />
            </div>
            <div>
              <strong>Enter your account details manually</strong><br />
              <Text type="secondary" style={{ fontSize: 13 }}>
                Enter your routing and account number directly.
              </Text>
            </div>
          </div>

          {entryMethod === 'manual' && (
            <Form form={form} layout="vertical" size="large" onClick={e => e.stopPropagation()}>
              <Form.Item
                label="Bank Name"
                name="bankName"
                rules={[{ required: true, message: 'Bank name is required' }]}
              >
                <Input
                  value={bankAccount.bankName}
                  onChange={e => updateField('bankName', e.target.value)}
                  placeholder="e.g. Chase, Bank of America"
                />
              </Form.Item>
              <Form.Item
                label="Routing Number"
                name="routingNumber"
                rules={[
                  { required: true, message: 'Routing number is required' },
                  { len: 9, message: 'Routing number must be 9 digits' },
                ]}
              >
                <Input
                  value={bankAccount.routingNumber}
                  onChange={e => updateField('routingNumber', e.target.value.replace(/\D/g, ''))}
                  placeholder="9-digit routing number"
                  maxLength={9}
                />
              </Form.Item>
              <Form.Item
                label="Account Number"
                name="accountNumber"
                rules={[{ required: true, message: 'Account number is required' }]}
              >
                <Input.Password
                  value={bankAccount.accountNumber}
                  onChange={e => updateField('accountNumber', e.target.value)}
                  placeholder="Account number"
                />
              </Form.Item>
              <Form.Item
                label="Confirm Account Number"
                name="confirmAccountNumber"
                rules={[
                  { required: true, message: 'Please confirm your account number' },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue('accountNumber') === value) {
                        return Promise.resolve()
                      }
                      return Promise.reject(new Error('Account numbers do not match'))
                    },
                  }),
                ]}
              >
                <Input.Password
                  value={bankAccount.confirmAccountNumber}
                  onChange={e => updateField('confirmAccountNumber', e.target.value)}
                  placeholder="Re-enter account number"
                />
              </Form.Item>

              <EducationBlock title="Where do I find my routing and account number?">
                <p style={{ margin: 0 }}>
                  You can find both numbers at the bottom of any check from your business bank account.
                  The routing number is the first 9 digits on the left, and the account number follows it.
                  You can also find these in your bank's online banking portal under account details.
                </p>
              </EducationBlock>
            </Form>
          )}
        </div>
      </div>
      <StepFooter
        onBack={onBack}
        onNext={handleNext}
        nextDisabled={!entryMethod || (entryMethod === 'plaid')}
      />
    </>
  )
}

export default BankAccountStep
